import { useState, useMemo } from 'react';
import {
  Warehouse,
  History,
  Search,
  Plus,
  AlertTriangle,
  Filter,
  Download,
  Boxes,
  Calendar,
  ClipboardList,
  ShoppingCart,
  PackageSearch,
  CheckCircle2,
  Truck,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { useInventory, MonthlyPlan } from '@/context/InventoryContext';
import { SaveIndicator } from '@/components/ui/save-indicator';
import { StatusCard } from '@/components/dashboard/StatusCard';
import {
  ProductItem,
  SENSOR_BOX_ID,
  CONTROL_BOX_ID,
} from '@/data/products';

// Transaction types for ledger
const transactionTypes = {
  PO_RECEIPT: { label: 'PO Receipt', color: 'bg-green-100 text-green-700' },
  WO_OUTPUT: { label: 'WO Output', color: 'bg-blue-100 text-blue-700' },
  WO_CONSUMPTION: { label: 'WO Consumption', color: 'bg-orange-100 text-orange-700' },
  ADJUSTMENT: { label: 'Adjustment', color: 'bg-gray-100 text-gray-700' },
  USED: { label: 'Used', color: 'bg-red-100 text-red-700' },
};

// Generate ledger entries from used data
function generateLedgerEntries(products: ProductItem[]) {
  const entries: any[] = [];
  let id = 1;
  const baseDate = new Date();

  products.filter(p => p.type === 'component').forEach((product, idx) => {
    if (product.startOnHand > 0) {
      entries.push({
        id: id++,
        date: new Date(baseDate.getTime() - (30 + idx) * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        time: '09:00',
        sku: product.sku,
        product_name: product.name,
        type: 'PO_RECEIPT',
        qty: product.startOnHand + product.used,
        reference: `PO-2024-${String(1000 + idx).padStart(4, '0')}`,
        location: 'Main Warehouse',
        user: 'System Import',
      });
    }

    if (product.used > 0) {
      entries.push({
        id: id++,
        date: new Date(baseDate.getTime() - idx * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        time: '14:30',
        sku: product.sku,
        product_name: product.name,
        type: 'USED',
        qty: -product.used,
        reference: `WO-2024-${String(2000 + idx).padStart(4, '0')}`,
        location: 'Production Floor',
        user: 'Production Team',
      });
    }
  });

  return entries.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
}

// Stock Adjustment Dialog
function StockAdjustmentDialog({
  open,
  onOpenChange,
  products,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  products: ProductItem[];
  onSubmit: (productId: number, adjustment: number, reason: string) => void;
}) {
  const [selectedProduct, setSelectedProduct] = useState<string>('');
  const [adjustment, setAdjustment] = useState<number>(0);
  const [reason, setReason] = useState<string>('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedProduct && adjustment !== 0) {
      onSubmit(Number(selectedProduct), adjustment, reason);
      setSelectedProduct('');
      setAdjustment(0);
      setReason('');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Adjust Stock</DialogTitle>
          <DialogDescription>
            Make a manual stock adjustment for a product.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="product" className="text-right">
                Product
              </Label>
              <Select value={selectedProduct} onValueChange={setSelectedProduct}>
                <SelectTrigger className="col-span-3">
                  <SelectValue placeholder="Select product" />
                </SelectTrigger>
                <SelectContent>
                  {products.filter(p => p.type === 'component').map((product) => (
                    <SelectItem key={product.id} value={product.id.toString()}>
                      {product.sku} - {product.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="adjustment" className="text-right">
                Adjustment
              </Label>
              <Input
                id="adjustment"
                type="number"
                value={adjustment}
                onChange={(e) => setAdjustment(Number(e.target.value))}
                className="col-span-3"
                placeholder="Positive to add, negative to remove"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="reason" className="text-right">
                Reason
              </Label>
              <Input
                id="reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="col-span-3"
                placeholder="Reason for adjustment"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={!selectedProduct || adjustment === 0}>
              Submit Adjustment
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function InventoryPage() {
  // Get global state from context
  const {
    products,
    monthlyPlan,
    setMonthlyPlan,
    purchaseOrders,
    saveStatus,
    inventoryUsed,
    setInventoryUsed,
    adjustInventory,
  } = useInventory();

  // Calculate ordered quantities from Purchase Orders with status 'submitted' (Order Received)
  const orderedFromPOs = useMemo(() => {
    const orderedMap = new Map<number, number>();
    purchaseOrders
      .filter(po => po.status === 'submitted')
      .forEach(po => {
        po.lines.forEach(line => {
          const currentQty = orderedMap.get(line.product_id) || 0;
          orderedMap.set(line.product_id, currentQty + line.ordered_qty);
        });
      });
    return orderedMap;
  }, [purchaseOrders]);

  // Local UI state
  const [searchQuery, setSearchQuery] = useState('');
  const [assemblyFilter, setAssemblyFilter] = useState<string>('all');
  const [selectedMonth, setSelectedMonth] = useState<string>('all');
  const [adjustDialogOpen, setAdjustDialogOpen] = useState(false);
  const { toast } = useToast();

  // Get components only (not assemblies)
  const components = useMemo(() =>
    products.filter(p => p.type === 'component'),
    [products]
  );

  // Calculate total systems planned
  const totalPlannedSystems = useMemo(() => {
    return monthlyPlan.reduce((acc, m) => ({
      sensorBox: acc.sensorBox + m.sensorBoxQty,
      controlBox: acc.controlBox + m.controlBoxQty,
    }), { sensorBox: 0, controlBox: 0 });
  }, [monthlyPlan]);

  // Get systems count for selected month
  const selectedMonthSystems = useMemo(() => {
    if (selectedMonth === 'all') {
      return totalPlannedSystems;
    }
    const month = monthlyPlan.find(m => m.month === selectedMonth);
    return month
      ? { sensorBox: month.sensorBoxQty, controlBox: month.controlBoxQty }
      : { sensorBox: 0, controlBox: 0 };
  }, [selectedMonth, monthlyPlan, totalPlannedSystems]);

  // Calculate requirements for each component
  const componentRequirements = useMemo(() => {
    return components.map(component => {
      // Get system count based on component's parent type
      const systemCount = component.parentType === 'sensor_box'
        ? selectedMonthSystems.sensorBox
        : selectedMonthSystems.controlBox;

      // Required = Qty/System × number of systems for selected period
      const required = component.qtyPerSystem * systemCount;
      // Get ordered quantity from Purchase Orders (Order Received status)
      const ordered = orderedFromPOs.get(component.id) || 0;
      // Get used quantity from separate inventory tracking (starts at 0, independent of products)
      const used = inventoryUsed.get(component.id) || 0;
      // On Hand = Ordered - Used (what remains in stock)
      const onHand = ordered - used;
      // To Order = Required - Ordered (what's missing to complete planned systems)
      const toOrder = Math.max(0, required - ordered);

      return {
        ...component,
        required,
        onHand,
        ordered,
        used,
        toOrder,
        status: toOrder > 0 ? 'need_order' : 'ok',
      };
    });
  }, [components, orderedFromPOs, selectedMonthSystems, inventoryUsed]);

  // Filter components
  const filteredRequirements = useMemo(() => {
    return componentRequirements.filter((item) => {
      const matchesSearch =
        item.sku.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.manufacturer.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesAssembly =
        assemblyFilter === 'all' ||
        (assemblyFilter === 'sensor_box' && item.parentId === SENSOR_BOX_ID) ||
        (assemblyFilter === 'control_box' && item.parentId === CONTROL_BOX_ID);

      return matchesSearch && matchesAssembly;
    });
  }, [componentRequirements, searchQuery, assemblyFilter]);

  // Generate ledger entries
  const ledgerEntries = useMemo(() => generateLedgerEntries(products), [products]);

  // Summary stats - from Requirements table columns
  const summaryStats = useMemo(() => {
    // Total Required = sum of "Required" column
    const totalRequired = componentRequirements.reduce(
      (sum, item) => sum + item.required,
      0
    );

    // Already Ordered = sum of "On Hand" column
    const totalOnHand = componentRequirements.reduce(
      (sum, item) => sum + item.onHand,
      0
    );

    // Total Ordered = sum of "Ordered" column
    const totalOrdered = componentRequirements.reduce(
      (sum, item) => sum + item.ordered,
      0
    );

    // Need to Order = sum of "To Order" column
    const totalToOrder = componentRequirements.reduce(
      (sum, item) => sum + item.toOrder,
      0
    );

    // Count systems already supplied (months with status 'completed')
    const alreadySupplied = monthlyPlan.reduce(
      (sum, m) => m.status === 'completed' ? sum + m.sensorBoxQty : sum,
      0
    );

    // Total systems required for supply tracking
    const totalSystemsRequired = monthlyPlan.reduce(
      (sum, m) => sum + m.sensorBoxQty,
      0
    );

    // Systems still need to supply
    const qtyToSupply = totalSystemsRequired - alreadySupplied;

    return { totalRequired, totalOnHand, totalOrdered, totalToOrder, alreadySupplied, qtyToSupply };
  }, [componentRequirements, monthlyPlan]);

  const updateMonthlyPlan = (monthIndex: number, field: keyof MonthlyPlan, value: number | string) => {
    setMonthlyPlan(prev => prev.map((m, i) => {
      if (i !== monthIndex) return m;
      if (field === 'sensorBoxQty' || field === 'controlBoxQty') {
        return { ...m, [field]: Math.max(0, value as number) };
      }
      return { ...m, [field]: value };
    }));
  };

  const handleAdjustment = async (productId: number, adjustment: number, _reason: string) => {
    const result = await adjustInventory(productId, adjustment);

    if (result.success) {
      toast({
        title: 'Stock adjusted',
        description: `Adjusted stock by ${adjustment > 0 ? '+' : ''}${adjustment} units.`,
      });
    } else {
      toast({
        title: 'Adjustment failed',
        description: result.error || 'Failed to adjust stock. Please try again.',
        variant: 'destructive',
      });
    }
    setAdjustDialogOpen(false);
  };


  // Update used quantity - separate from products, stored in inventoryUsed map
  const updateUsedQty = (productId: number, quantity: number) => {
    const newValue = Math.max(0, quantity);
    setInventoryUsed(prev => {
      const newMap = new Map(prev);
      newMap.set(productId, newValue);
      return newMap;
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-bold">Inventory & Planning</h2>
            <SaveIndicator status={saveStatus} />
          </div>
          <p className="text-muted-foreground">
            Plan monthly production and track component requirements
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => setAdjustDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Adjust Stock
          </Button>
        </div>
      </div>

      {/* Summary Cards - Modern Floating Design */}
      <div className="grid gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
        <StatusCard
          title="Total Required"
          value={summaryStats.totalRequired.toLocaleString()}
          subtext="Sum of Required column"
          trend="neutral"
          type="custom"
          icon={ClipboardList}
          iconColor="text-blue-500"
          iconBgColor="bg-blue-500/10"
          gradientColor="blue"
        />

        <StatusCard
          title="On Hand"
          value={summaryStats.totalOnHand.toLocaleString()}
          subtext="Sum of On Hand column"
          trend={summaryStats.totalOnHand >= 0 ? 'up' : 'down'}
          type="custom"
          icon={ShoppingCart}
          iconColor="text-green-500"
          iconBgColor="bg-green-500/10"
          gradientColor="green"
        />

        <StatusCard
          title="Need to Order"
          value={summaryStats.totalToOrder.toLocaleString()}
          subtext={summaryStats.totalToOrder > 0 ? 'Action needed' : 'All ordered'}
          trend={summaryStats.totalToOrder > 0 ? 'down' : 'neutral'}
          type="custom"
          icon={PackageSearch}
          iconColor="text-orange-500"
          iconBgColor="bg-orange-500/10"
          gradientColor="orange"
        />

        <StatusCard
          title="Already Supplied"
          value={summaryStats.alreadySupplied.toString()}
          subtext="Completed"
          trend="up"
          type="custom"
          icon={CheckCircle2}
          iconColor="text-emerald-500"
          iconBgColor="bg-emerald-500/10"
          gradientColor="emerald"
        />

        <StatusCard
          title="Qty to Supply"
          value={summaryStats.qtyToSupply.toString()}
          subtext={summaryStats.qtyToSupply > 0 ? 'Pending' : 'All done'}
          trend={summaryStats.qtyToSupply > 0 ? 'neutral' : 'up'}
          type="custom"
          icon={Truck}
          iconColor="text-cyan-500"
          iconBgColor="bg-cyan-500/10"
          gradientColor="cyan"
        />
      </div>

      {/* Tabs */}
      <Tabs defaultValue="requirements" className="space-y-4">
        <TabsList>
          <TabsTrigger value="requirements" className="gap-2">
            <Warehouse className="h-4 w-4" />
            Requirements
          </TabsTrigger>
          <TabsTrigger value="planning" className="gap-2">
            <Calendar className="h-4 w-4" />
            Monthly Planning
          </TabsTrigger>
          <TabsTrigger value="ledger" className="gap-2">
            <History className="h-4 w-4" />
            Stock Ledger
          </TabsTrigger>
        </TabsList>

        <TabsContent value="planning" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Monthly System Production Plan</CardTitle>
              <CardDescription>
                Enter the number of systems you plan to build each month. The system will calculate required components.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="px-4 py-3 text-left text-sm font-medium">Month</th>
                      <th className="px-4 py-3 text-center text-sm font-medium">Sensor Box Systems</th>
                      <th className="px-4 py-3 text-center text-sm font-medium">Control Box Systems</th>
                      <th className="px-4 py-3 text-center text-sm font-medium">Total Systems</th>
                      <th className="px-4 py-3 text-center text-sm font-medium">SMC PO Number</th>
                      <th className="px-4 py-3 text-center text-sm font-medium">Supply Date</th>
                      <th className="px-4 py-3 text-center text-sm font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {monthlyPlan.map((month, idx) => (
                      <tr key={month.month} className="border-b">
                        <td className="px-4 py-3 text-sm font-medium">{month.label}</td>
                        <td className="px-4 py-3">
                          <Input
                            type="number"
                            min="0"
                            value={month.sensorBoxQty}
                            onChange={(e) => updateMonthlyPlan(idx, 'sensorBoxQty', Number(e.target.value))}
                            className="w-24 mx-auto text-center"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <Input
                            type="number"
                            min="0"
                            value={month.controlBoxQty}
                            onChange={(e) => updateMonthlyPlan(idx, 'controlBoxQty', Number(e.target.value))}
                            className="w-24 mx-auto text-center"
                          />
                        </td>
                        <td className="px-4 py-3 text-center font-medium">
                          {month.sensorBoxQty + month.controlBoxQty}
                        </td>
                        <td className="px-4 py-3">
                          <Input
                            type="text"
                            value={month.smcPoNumber}
                            onChange={(e) => updateMonthlyPlan(idx, 'smcPoNumber', e.target.value)}
                            placeholder="PO-XXXX"
                            className="w-32 mx-auto text-center"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <Input
                            type="date"
                            value={month.supplyDate}
                            onChange={(e) => updateMonthlyPlan(idx, 'supplyDate', e.target.value)}
                            className="w-36 mx-auto text-center"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <Select
                            value={month.status}
                            onValueChange={(value) => updateMonthlyPlan(idx, 'status', value)}
                          >
                            <SelectTrigger className="w-28 mx-auto">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pending">Pending</SelectItem>
                              <SelectItem value="completed">Completed</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                      </tr>
                    ))}
                    <tr className="bg-muted/30 font-semibold">
                      <td className="px-4 py-3 text-sm">Total</td>
                      <td className="px-4 py-3 text-center">{totalPlannedSystems.sensorBox}</td>
                      <td className="px-4 py-3 text-center">{totalPlannedSystems.controlBox}</td>
                      <td className="px-4 py-3 text-center">
                        {totalPlannedSystems.sensorBox + totalPlannedSystems.controlBox}
                      </td>
                      <td className="px-4 py-3"></td>
                      <td className="px-4 py-3"></td>
                      <td className="px-4 py-3"></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Quick Summary */}
          {(totalPlannedSystems.sensorBox > 0 || totalPlannedSystems.controlBox > 0) && (
            <Card>
              <CardHeader>
                <CardTitle>Planning Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="p-4 rounded-lg border bg-muted/20">
                    <div className="flex items-center gap-2 mb-2">
                      <Boxes className="h-5 w-5 text-primary" />
                      <span className="font-medium">Sensor Box</span>
                    </div>
                    <div className="text-3xl font-bold">{totalPlannedSystems.sensorBox}</div>
                    <p className="text-sm text-muted-foreground">systems planned</p>
                  </div>
                  <div className="p-4 rounded-lg border bg-muted/20">
                    <div className="flex items-center gap-2 mb-2">
                      <Boxes className="h-5 w-5 text-primary" />
                      <span className="font-medium">Control Box</span>
                    </div>
                    <div className="text-3xl font-bold">{totalPlannedSystems.controlBox}</div>
                    <p className="text-sm text-muted-foreground">systems planned</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="requirements" className="space-y-4">
          {/* Filters */}
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search by SKU, name, or manufacturer..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            <div className="flex gap-2">
              <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                <SelectTrigger className="w-[180px]">
                  <Calendar className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Select period" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Full Review (All Months)</SelectItem>
                  {monthlyPlan.map((month) => (
                    <SelectItem key={month.month} value={month.month}>
                      {month.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={assemblyFilter} onValueChange={setAssemblyFilter}>
                <SelectTrigger className="w-[180px]">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Assembly" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Assemblies</SelectItem>
                  <SelectItem value="sensor_box">Sensor Box</SelectItem>
                  <SelectItem value="control_box">Control Box</SelectItem>
                </SelectContent>
              </Select>

              <Button variant="outline" size="icon">
                <Download className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Period Summary */}
          <Card className="bg-muted/30">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div>
                    <span className="text-sm text-muted-foreground">Viewing: </span>
                    <span className="font-medium">
                      {selectedMonth === 'all'
                        ? 'Full Review (All Months)'
                        : monthlyPlan.find(m => m.month === selectedMonth)?.label}
                    </span>
                  </div>
                  <div className="h-4 w-px bg-border" />
                  <div className="flex gap-4 text-sm">
                    <span>
                      <span className="text-muted-foreground">Sensor Box: </span>
                      <span className="font-medium">{selectedMonthSystems.sensorBox} systems</span>
                    </span>
                    <span>
                      <span className="text-muted-foreground">Control Box: </span>
                      <span className="font-medium">{selectedMonthSystems.controlBox} systems</span>
                    </span>
                  </div>
                </div>
                <div className="text-sm">
                  <span className="text-muted-foreground">Total Systems: </span>
                  <span className="font-bold text-primary">
                    {selectedMonthSystems.sensorBox + selectedMonthSystems.controlBox}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Requirements Table */}
          <div className="rounded-lg border bg-card">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="px-4 py-3 text-left text-sm font-medium">Part Number</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Name</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Manufacturer</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Assembly</th>
                    <th className="px-4 py-3 text-right text-sm font-medium">Required</th>
                    <th className="px-4 py-3 text-right text-sm font-medium">Ordered</th>
                    <th className="px-4 py-3 text-right text-sm font-medium">Used</th>
                    <th className="px-4 py-3 text-right text-sm font-medium">On Hand</th>
                    <th className="px-4 py-3 text-right text-sm font-medium">To Order</th>
                    <th className="px-4 py-3 text-center text-sm font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRequirements.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="px-4 py-8 text-center text-muted-foreground">
                        No items found.
                      </td>
                    </tr>
                  ) : (
                    filteredRequirements.map((item) => (
                      <tr
                        key={item.id}
                        className={cn(
                          'border-b hover:bg-muted/30 transition-colors',
                          item.toOrder > 0 && 'bg-yellow-500/5'
                        )}
                      >
                        <td className="px-4 py-3 text-sm font-mono">{item.sku}</td>
                        <td className="px-4 py-3 text-sm font-medium">{item.name}</td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">{item.manufacturer || '-'}</td>
                        <td className="px-4 py-3 text-sm">
                          <Badge variant={item.parentType === 'sensor_box' ? 'default' : 'secondary'}>
                            {item.parentType === 'sensor_box' ? 'Sensor Box' : 'Control Box'}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-sm text-right font-medium">
                          {item.required}
                        </td>
                        <td className="px-4 py-3 text-sm text-right font-medium text-blue-600">
                          {item.ordered}
                        </td>
                        <td className="px-4 py-3 text-sm text-right">
                          <Input
                            type="number"
                            min="0"
                            value={item.used}
                            onChange={(e) => updateUsedQty(item.id, Number(e.target.value))}
                            className="w-20 text-center ml-auto"
                          />
                        </td>
                        <td className={cn(
                          'px-4 py-3 text-sm text-right font-medium',
                          item.onHand < 0 && 'text-destructive',
                          item.onHand > 0 && 'text-green-600'
                        )}>
                          {item.onHand}
                        </td>
                        <td className={cn(
                          'px-4 py-3 text-sm text-right font-bold',
                          item.toOrder > 0 ? 'text-red-600' : 'text-green-600'
                        )}>
                          {item.toOrder}
                        </td>
                        <td className="px-4 py-3 text-sm text-center">
                          {item.toOrder > 0 ? (
                            <Badge variant="warning" className="gap-1">
                              <AlertTriangle className="h-3 w-3" />
                              Need {item.toOrder}
                            </Badge>
                          ) : item.required === 0 ? (
                            <Badge variant="secondary">No Demand</Badge>
                          ) : (
                            <Badge variant="success">OK</Badge>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="ledger" className="space-y-4">
          {/* Ledger Filters */}
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input placeholder="Search transactions..." className="pl-10" />
            </div>

            <div className="flex gap-2">
              <Select defaultValue="all">
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Transaction Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {Object.entries(transactionTypes).map(([key, { label }]) => (
                    <SelectItem key={key} value={key}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button variant="outline" size="icon">
                <Download className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Ledger Table */}
          <div className="rounded-lg border bg-card">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="px-4 py-3 text-left text-sm font-medium">Date/Time</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Part Number</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Product</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Type</th>
                    <th className="px-4 py-3 text-right text-sm font-medium">Qty</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Reference</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Location</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">User</th>
                  </tr>
                </thead>
                <tbody>
                  {ledgerEntries.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">
                        No transactions found
                      </td>
                    </tr>
                  ) : (
                    ledgerEntries.slice(0, 50).map((entry) => {
                      const typeInfo = transactionTypes[entry.type as keyof typeof transactionTypes];

                      return (
                        <tr key={entry.id} className="border-b hover:bg-muted/30">
                          <td className="px-4 py-3 text-sm">
                            <div>{entry.date}</div>
                            <div className="text-xs text-muted-foreground">{entry.time}</div>
                          </td>
                          <td className="px-4 py-3 text-sm font-mono">{entry.sku}</td>
                          <td className="px-4 py-3 text-sm">{entry.product_name}</td>
                          <td className="px-4 py-3 text-sm">
                            <span
                              className={cn(
                                'inline-flex items-center rounded-full px-2 py-1 text-xs font-medium',
                                typeInfo?.color
                              )}
                            >
                              {typeInfo?.label || entry.type}
                            </span>
                          </td>
                          <td
                            className={cn(
                              'px-4 py-3 text-sm text-right font-medium',
                              entry.qty > 0 ? 'text-green-600' : 'text-red-600'
                            )}
                          >
                            {entry.qty > 0 ? `+${entry.qty}` : entry.qty}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <span className="font-mono text-primary">
                              {entry.reference}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-muted-foreground">{entry.location}</td>
                          <td className="px-4 py-3 text-sm text-muted-foreground">{entry.user}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <StockAdjustmentDialog
        open={adjustDialogOpen}
        onOpenChange={setAdjustDialogOpen}
        products={products}
        onSubmit={handleAdjustment}
      />
    </div>
  );
}
